import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Wifi, WifiOff, Package, TrendingUp, Star, Clock, MapPin,
  RefreshCw, AlertCircle, Wallet, DollarSign,
} from 'lucide-react';
import { deliveryAPI } from '../../services/api';
import { formatPrice } from '../../utils/helpers';
import DeliveryLayout from '../../components/delivery/DeliveryLayout';
import { toast } from '../../hooks/use-toast';

const DeliveryDashboard = () => {
  const [stats, setStats] = useState(null);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);

  const deliveryUser = JSON.parse(localStorage.getItem('delivery_user') || '{}');
  const isVerified = deliveryUser?.delivery_profile?.is_verified || false;
  const verificationStatus = deliveryUser?.delivery_profile?.verification_status || 'pending';

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const statsData = await deliveryAPI.getStats();
      setStats(statsData);
      setIsOnline(statsData.is_online);
      if (statsData.is_verified) {
        const ordersData = await deliveryAPI.getAvailableOrders();
        setAvailableOrders(ordersData.slice(0, 5));
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleOnlineStatus = async () => {
    if (!isVerified) {
      toast({ title: 'Cuenta no verificada', variant: 'destructive' });
      return;
    }
    setTogglingStatus(true);
    try {
      const newStatus = !isOnline;
      await deliveryAPI.updateStatus(newStatus);
      setIsOnline(newStatus);
      const user = JSON.parse(localStorage.getItem('delivery_user') || '{}');
      if (user.delivery_profile) {
        user.delivery_profile.is_online = newStatus;
        localStorage.setItem('delivery_user', JSON.stringify(user));
      }
      toast({ title: newStatus ? 'Conectado!' : 'Desconectado' });
    } catch {
      toast({ title: 'Error', variant: 'destructive' });
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleAcceptOrder = async (orderId) => {
    try {
      await deliveryAPI.acceptOrder(orderId);
      toast({ title: 'Pedido aceptado!', description: 'Ve a "Mis Pedidos" para ver detalles' });
      loadData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'No se pudo aceptar', variant: 'destructive' });
    }
  };

  if (loading) {
    return <DeliveryLayout><div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 text-orange-500 animate-spin" /></div></DeliveryLayout>;
  }

  return (
    <DeliveryLayout>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Verification Banner */}
        {!isVerified && (
          <Card className={`p-4 ${verificationStatus === 'pending' ? 'bg-yellow-900/30 border-yellow-700' : 'bg-red-900/30 border-red-700'}`}>
            <div className="flex items-start gap-3">
              <AlertCircle className={`w-6 h-6 flex-shrink-0 ${verificationStatus === 'pending' ? 'text-yellow-400' : 'text-red-400'}`} />
              <div>
                <h3 className="font-semibold text-white">{verificationStatus === 'pending' ? 'Cuenta en revision' : 'Cuenta rechazada'}</h3>
                <p className="text-sm text-gray-400 mt-1">
                  {verificationStatus === 'pending' ? 'Tu cuenta esta siendo revisada.' : 'Contacta a soporte.'}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Online Toggle */}
        <Card className="bg-gray-800 border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-4 rounded-full ${isOnline ? 'bg-green-500' : 'bg-gray-600'}`}>
                {isOnline ? <Wifi className="w-8 h-8 text-white" /> : <WifiOff className="w-8 h-8 text-white" />}
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{isOnline ? 'En linea' : 'Desconectado'}</h2>
                <p className="text-gray-400">{isOnline ? 'Recibiendo pedidos' : 'No recibiras pedidos'}</p>
              </div>
            </div>
            <Button onClick={toggleOnlineStatus} disabled={togglingStatus || !isVerified}
              className={`px-8 py-6 text-lg font-semibold ${isOnline ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}`}>
              {togglingStatus ? <RefreshCw className="w-5 h-5 animate-spin" /> : isOnline ? 'Desconectar' : 'Conectar'}
            </Button>
          </div>
        </Card>

        {/* Stats + Wallet Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-gray-800 border-gray-700 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg"><Package className="w-5 h-5 text-orange-500" /></div>
              <div>
                <p className="text-2xl font-bold text-white">{stats?.today_deliveries || 0}</p>
                <p className="text-xs text-gray-400">Hoy</p>
              </div>
            </div>
          </Card>
          <Card className="bg-gray-800 border-gray-700 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg"><TrendingUp className="w-5 h-5 text-blue-500" /></div>
              <div>
                <p className="text-2xl font-bold text-white">{stats?.total_deliveries || 0}</p>
                <p className="text-xs text-gray-400">Total</p>
              </div>
            </div>
          </Card>
          <Card className="bg-gray-800 border-gray-700 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg"><Star className="w-5 h-5 text-yellow-500" /></div>
              <div>
                <p className="text-2xl font-bold text-white">{stats?.rating?.toFixed(1) || '5.0'}</p>
                <p className="text-xs text-gray-400">Rating</p>
              </div>
            </div>
          </Card>
          <Card className="bg-gray-800 border-gray-700 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg"><Clock className="w-5 h-5 text-purple-500" /></div>
              <div>
                <p className="text-2xl font-bold text-white">{stats?.pending_deliveries || 0}</p>
                <p className="text-xs text-gray-400">Pendientes</p>
              </div>
            </div>
          </Card>
          {/* WALLET */}
          <Card className="bg-gradient-to-br from-green-900/40 to-emerald-900/40 border-green-700/50 p-4 col-span-2 md:col-span-1" data-testid="driver-wallet">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg"><Wallet className="w-5 h-5 text-green-400" /></div>
              <div>
                <p className="text-2xl font-bold text-green-400">{formatPrice(stats?.wallet_balance || 0)}</p>
                <p className="text-xs text-gray-400">Mi Billetera</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Available Orders with earnings & distance */}
        {isVerified && isOnline && (
          <Card className="bg-gray-800 border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Pedidos disponibles</h3>
              <Button variant="ghost" size="sm" onClick={loadData} className="text-gray-400 hover:text-white"><RefreshCw className="w-4 h-4" /></Button>
            </div>
            {availableOrders.length === 0 ? (
              <div className="text-center py-8">
                <Package className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No hay pedidos disponibles</p>
              </div>
            ) : (
              <div className="space-y-3">
                {availableOrders.map((order) => (
                  <div key={order.order_id} className="bg-gray-700/50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-orange-500/20 rounded-lg"><MapPin className="w-5 h-5 text-orange-500" /></div>
                        <div>
                          <p className="text-white font-medium">Pedido #{(order.order_id || '').slice(0, 6).toUpperCase()}</p>
                          <p className="text-sm text-gray-400">{order.items?.length || 0} productos</p>
                          <p className="text-xs text-gray-500 mt-0.5">{order.shipping_address?.city || order.shipping_address?.address || 'Sin direccion'}</p>
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-2">
                        <div>
                          <p className="text-lg font-black text-green-400">+{formatPrice(35)}</p>
                          <p className="text-[10px] text-gray-500">ganancia</p>
                        </div>
                        {order.heisenberg_distance_km && (
                          <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">
                            ~{order.heisenberg_distance_km} km
                          </span>
                        )}
                        <Button size="sm" onClick={() => handleAcceptOrder(order.order_id)} className="bg-green-500 hover:bg-green-600">
                          Aceptar
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>
    </DeliveryLayout>
  );
};

export default DeliveryDashboard;
